import React, { useState } from 'react';
import { Search, Sparkles, Loader2 } from 'lucide-react';
import { traqPackAPI } from '../lib/supabase';
import { useSupabaseAuth } from '../contexts/SupabaseAuthContext';

interface AIQueryBarProps {
  onQueryResult: (data: any[]) => void;
  compact?: boolean;
}

export function AIQueryBar({ onQueryResult, compact = false }: AIQueryBarProps) {
  const [query, setQuery] = useState('');
  const [response, setResponse] = useState('');
  const [loading, setLoading] = useState(false);
  const [showResponse, setShowResponse] = useState(false);
  const { user } = useSupabaseAuth();

  const handleQuery = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!query.trim() || loading) return;

    setLoading(true);
    setShowResponse(true);
    
    try {
      console.log('Sending AI query:', query, 'User ID:', user?.id);
      
      // Use the traqPackAPI which handles the Edge Function call properly
      const response = await traqPackAPI.queryWithAI(query);
      
      console.log('AI query response:', response);
      
      setResponse(response.response);
      
      // Update map with GPS data
      if (response.data && response.data.length > 0) {
        onQueryResult(response.data);
      }
    } catch (error: any) {
      console.error('AI query failed:', error);
      // Provide more detailed error message
      if (error?.message?.includes('Failed to fetch')) {
        setResponse('Unable to connect to the AI service. Please check your internet connection and try again.');
      } else if (error?.message?.includes('GEMINI_API_KEY')) {
        setResponse('AI service is not configured properly. Please contact support.');
      } else if (error?.response?.data?.error) {
        setResponse(`Error: ${error.response.data.error}`);
      } else {
        setResponse(`Sorry, I couldn't process your query. Error: ${error?.message || 'Unknown error'}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const exampleQueries = [
    "Where is the package I sent last week?",
    "Show me all devices currently in transit",
    "Which tracker passed through Chicago yesterday?",
    "Find my device that left New York on Monday"
  ];

  if (compact) {
    return (
      <div className="w-full">
        <form onSubmit={handleQuery} className="relative">
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Ask AI about devices..."
            className="w-full pl-10 pr-10 py-2 bg-gray-50 border border-gray-300 rounded-lg text-sm text-gray-900 placeholder-gray-500 focus:ring-2 focus:ring-traqpack-yellow/50 focus:border-traqpack-yellow"
          />
          <Sparkles className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-traqpack-yellow" />
          <button 
            type="submit" 
            disabled={loading || !query.trim()}
            className="absolute right-2 top-1/2 transform -translate-y-1/2 p-1 disabled:opacity-50"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin text-gray-500" />
            ) : (
              <Search className="w-4 h-4 text-gray-500 hover:text-gray-700" />
            )}
          </button>
        </form>
        
        {showResponse && response && (
          <div className="mt-3 p-3 bg-traqpack-yellow/5 border border-traqpack-yellow/20 rounded-lg">
            <p className="text-xs text-gray-700 leading-relaxed">{response}</p>
            <button
              onClick={() => {
                setShowResponse(false);
                setResponse('');
                setQuery('');
              }}
              className="text-xs text-gray-500 hover:text-gray-700 mt-2"
            >
              Clear
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="ai-query-container">
      <form onSubmit={handleQuery} className="ai-query-bar apple-card">
        <div className="flex items-center gap-3 p-4">
          <Sparkles className="text-traqpack-yellow w-5 h-5" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Ask about your devices in natural language..."
            className="flex-1 bg-transparent outline-none text-gray-800 placeholder-gray-400"
          />
          <button 
            type="submit" 
            disabled={loading || !query.trim()}
            className="apple-button p-2 disabled:opacity-50"
          >
            {loading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Search className="w-5 h-5" />
            )}
          </button>
        </div>
        
        {!showResponse && (
          <div className="px-4 pb-3">
            <p className="text-xs text-gray-500 mb-2">Try asking:</p>
            <div className="flex flex-wrap gap-2">
              {exampleQueries.map((example, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={() => setQuery(example)}
                  className="text-xs px-3 py-1 bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
                >
                  {example}
                </button>
              ))}
            </div>
          </div>
        )}
      </form>

      {showResponse && response && (
        <div className="ai-response apple-card mt-4 p-4">
          <div className="flex items-start gap-3">
            <Sparkles className="text-traqpack-yellow w-5 h-5 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm text-gray-700 whitespace-pre-wrap">{response}</p>
              {/* Show battery bar if the latest response represents a battery result and we have data on the map handler side */}
              {/* The bar here is best-effort; the definitive battery bar is shown in AIChatPanel where we have response.intent+data */}
              <button
                onClick={() => {
                  setShowResponse(false);
                  setResponse('');
                  setQuery('');
                }}
                className="text-xs text-gray-500 hover:text-gray-700 mt-2"
              >
                Clear
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}